import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { history } from '@umijs/max';
import { flushSync } from 'react-dom';
import { useIntl, useModel } from 'umi';
import { login as loginApi, currentUser as getCurrentUserApi } from '@/services/ant-design-pro/api';
import { useAuthStore, type AdminUser } from '@/stores/authStore';
import { adminUserToCurrentUser } from '@/utils/authInitialState';
import { isSuperadminRole } from '@/utils/permissions';
import createAccess from '@/access';

type AppInitialState = Awaited<
  ReturnType<typeof import('@/app').getInitialState>
>;

/**
 * Post-login landing candidates (first match wins). Each entry maps a concrete page to the
 * `src/access.ts` boolean that authorizes it — the SAME flags `config/routes.ts` and the
 * app.tsx route guard use — so the chosen landing page never 403s or bounces. Checking the
 * access object (not raw permission strings) keeps this granular-aware: the backend issues
 * codes like `order-statistics:view`, never the legacy `orders:read`.
 *
 * Operation Data is first (preferred) but is permission-gated like every other page; an admin
 * with no accessible page lands on `/no-access`.
 */
const LANDING_ORDER: { path: string; accessKey: string }[] = [
  { path: '/operation/operation-data', accessKey: 'canViewOperationData' },
  { path: '/orders/statistics', accessKey: 'canViewOrderStatistics' },
  { path: '/storehouse/inspection-list', accessKey: 'canViewInspectionList' },
  { path: '/storehouse/rack', accessKey: 'canViewRacks' },
  { path: '/storehouse/warehouse-address', accessKey: 'canViewWarehouseAddress' },
  { path: '/storehouse/waybill-informations', accessKey: 'canViewWaybillInformations' },
  { path: '/storehouse/waybill-bandwidth-management', accessKey: 'canViewWaybillBandwidth' },
  { path: '/storehouse/transportation-methods', accessKey: 'canViewTransportationMethods' },
  { path: '/members/statistics', accessKey: 'canViewMemberStatistics' },
  { path: '/statistics/visit-analytics', accessKey: 'canViewSiteAnalytics' },
  { path: '/settlement/deposit-application-history', accessKey: 'canViewDepositApplicationHistory' },
  { path: '/settlement/deposit-usage-history', accessKey: 'canViewDepositUsageHistory' },
  { path: '/settlement/financial-record', accessKey: 'canViewFinancialRecords' },
  { path: '/settlement/pending-payment', accessKey: 'canViewPendingPayment' },
  { path: '/settlement/share-rebates', accessKey: 'canViewShareRebates' },
  { path: '/cs/inquiries', accessKey: 'canProcessGeneralInquiry' },
  { path: '/cs/notices', accessKey: 'canManageCS' },
  { path: '/cs/help', accessKey: 'canViewHelpCenter' },
  { path: '/settings/admins', accessKey: 'canViewAdmins' },
  { path: '/settings/rates', accessKey: 'canViewExchangeTariffSetting' },
  { path: '/settings/additional-service', accessKey: 'canViewAddOnServices' },
  { path: '/settings/island-mountain-cost', accessKey: 'canViewIslandMountainCost' },
  { path: '/settings/work-log', accessKey: 'canViewWorkerLog' },
  { path: '/settings/product-price-increase', accessKey: 'canViewProductPriceIncrease' },
];

/** Exported so layout/runtime can reuse the same landing logic as login redirect. */
export function getAccessibleRoute(user: AdminUser | null | undefined, desired?: string): string {
  if (isSuperadminRole(user?.role)) {
    return desired && desired !== '/' ? desired : '/operation/operation-data';
  }

  // Same access object that config/routes.ts + the app.tsx guard evaluate. createAccess()
  // reads the Zustand store (already updated by setAuth before login redirect runs), falling
  // back to the passed user.
  const access = createAccess({
    currentUser: user
      ? ({ role: user.role, permissions: user.permissions } as never)
      : undefined,
  }) as Record<string, unknown>;

  const canAccessPath = (path: string) => {
    const entry = LANDING_ORDER.find((r) => path.startsWith(r.path));
    if (!entry) return true; // unknown / detail page — not gated by this landing map
    return !!access[entry.accessKey];
  };

  if (desired && desired !== '/' && canAccessPath(desired)) return desired;

  const fallback = LANDING_ORDER.find((r) => !!access[r.accessKey]);
  // No accessible page → permission-free notice page (never bounce to login or a 403 page).
  return fallback?.path || '/no-access';
}

/**
 * Login mutation hook
 */
export const useLogin = () => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { setAuth } = useAuthStore();
  const { setInitialState } = useModel('@@initialState') || {};

  return useMutation({
    mutationFn: async (credentials: {
      identifier?: string;
      email?: string;
      password: string;
      autoLogin?: boolean;
    }) => {
      const response = await loginApi({
        identifier: credentials.identifier ?? credentials.email,
        email: credentials.email,
        password: credentials.password,
      });
      return response;
    },
    onSuccess: (data, variables) => {
      if (data.status === 'success' && data.data?.token && data.data?.admin) {
        const admin = data.data.admin;
        
        // Map admin data to AdminUser format
        const user: AdminUser = {
          _id: admin._id || '',
          email: admin.email || '',
          name: admin.name || '',
          role: admin.role || '',
          permissions: admin.permissions || [],
          isActive: admin.isActive ?? true,
          createdAt: admin.createdAt,
          updatedAt: admin.updatedAt,
          lastLogin: admin.lastLogin,
        };

        setAuth(data.data.token, user, variables.autoLogin !== false);

        // Umi's access plugin memoizes `accessFactory(initialState)` on initialState
        // only. Without syncing currentUser here, the first post-login navigation still
        // uses the pre-login access snapshot (no permissions) and shows 403 until refresh.
        if (setInitialState) {
          const currentUser = adminUserToCurrentUser(user);
          flushSync(() => {
            setInitialState((prev: AppInitialState | undefined) => ({
              ...(prev || {}),
              currentUser,
            }));
          });
        }
        
        // Invalidate and refetch user queries
        queryClient.invalidateQueries({ queryKey: ['currentUser'] });
        
        message.success(data.message || intl.formatMessage({ id: 'pages.auth.loginSuccess' }));
        
        // Redirect — pick a page the user actually has permission for so the
        // landing page's queries don't immediately return 403.
        const urlParams = new URL(window.location.href).searchParams;
        let desired = urlParams.get('redirect') || '';

        if (desired.startsWith('/admin/')) {
          desired = desired.replace(/^\/admin/, '');
        }
        if (desired && !desired.startsWith('/')) {
          desired = `/${desired}`;
        }

        const redirectPath = getAccessibleRoute(user, desired);
        history.push(redirectPath);
      } else {
        // console.error('❌ Login response missing required data:', {
        //   hasToken: !!data.data?.token,
        //   hasAdmin: !!data.data?.admin,
        //   status: data.status,
        // });
        message.error(data.message || intl.formatMessage({ id: 'pages.auth.loginFailed' }));
      }
    },
    onError: (error: any) => {
      // console.error('❌ Login mutation error:', error);
      const errorMessage = 
        error?.response?.data?.message || 
        error?.message || 
        intl.formatMessage({ id: 'pages.auth.loginFailedWithConsole' });
      message.error(errorMessage);
    },
  });
};

/**
 * Get current user query hook
 */
export const useCurrentUser = () => {
  const { token, user, isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['currentUser', token],
    queryFn: async () => {
      if (!token) {
        throw new Error('No token available');
      }
      const response = await getCurrentUserApi({
        skipErrorHandler: true,
      });
      return response.data;
    },
    enabled: !!token && isAuthenticated,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Logout mutation hook
 */
export const useLogout = () => {
  const queryClient = useQueryClient();
  const { clearAuth } = useAuthStore();
  const { setInitialState } = useModel('@@initialState') || {};

  return useMutation({
    mutationFn: async () => {
      // Call logout API if needed
      // await outLogin();
      return true;
    },
    onSuccess: () => {
      clearAuth();
      if (setInitialState) {
        setInitialState((prev: AppInitialState | undefined) => ({
          ...(prev || {}),
          currentUser: undefined,
        }));
      }
      queryClient.clear();
      const { search, pathname } = window.location;
      const urlParams = new URL(window.location.href).searchParams;
      const searchParams = new URLSearchParams({
        redirect: pathname + search,
      });
      const redirect = urlParams.get('redirect');
      if (window.location.pathname !== '/user/login' && !redirect) {
        history.replace(`/user/login?${searchParams.toString()}`);
      }
    },
  });
};

























